/**
 * ScheduleStatusBannerPure
 *
 * Pure presentational component for schedule status messages.
 * No server dependencies - all data passed via props.
 */
import { ButtonLink } from '../Input2';
import { Alerts, Time } from '../StrokeIcons';

// ============================================================================
// Types
// ============================================================================

export type ScheduleStatus = {
  isConfigured: boolean;
  isWithinSchedule: boolean;
  nextWindowStartMs: number | null;
  nextWindowStartFormatted: string | null;
  settingsUrl: string;
};

export type ScheduleStatusBannerPureProps = {
  scheduleStatus: ScheduleStatus | undefined | null;
};

// ============================================================================
// Component
// ============================================================================

export function ScheduleStatusBannerPure({ scheduleStatus }: ScheduleStatusBannerPureProps) {
  if (!scheduleStatus) return null;

  if (!scheduleStatus.isConfigured) {
    return (
      <div
        className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
        data-testid="schedule-status-banner">
        <Alerts.AlertTriangle className="size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
        <span className="min-w-0 flex-1 truncate">No LinkedIn schedule — nothing will send until you set one.</span>
        <ButtonLink
          href={scheduleStatus.settingsUrl}
          text
          className="shrink-0 whitespace-nowrap text-xs font-medium text-amber-700 underline hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100">
          Set schedule
        </ButtonLink>
      </div>
    );
  }
  if (!scheduleStatus.isWithinSchedule) {
    return (
      <div
        className="flex items-center gap-2 border-b border-blue-200 bg-blue-50 px-4 py-1.5 text-xs text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200"
        data-testid="schedule-status-banner">
        <Time.Clock className="size-3.5 shrink-0 text-blue-600 dark:text-blue-400" />
        <span className="min-w-0 flex-1 truncate">
          Sends resume{' '}
          <span className="font-medium">
            {scheduleStatus.nextWindowStartFormatted ?? 'at your next scheduled time'}
          </span>
        </span>
        <ButtonLink
          href={scheduleStatus.settingsUrl}
          text
          className="shrink-0 whitespace-nowrap text-xs font-medium text-blue-700 underline hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-100">
          Schedule
        </ButtonLink>
      </div>
    );
  }
  return null;
}
